import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/common/Login';
import PendingApproval from './components/common/PendingApproval';
import AdminDashboard from './components/admin/AdminDashboard';
import ClientDashboard from './components/client/ClientDashboard';
import './App.css';

// Protected route wrapper
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { currentUser, userProfile, loading } = useAuth();

  // --- CAMBIO AQUÍ: Solo mostrar pantalla de carga si `loading` es true ---
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }
  // --- FIN CAMBIO ---

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  // userProfile still loading from Firestore - wait
  if (!userProfile) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (userProfile?.status === "pending") {
    return <Navigate to="/pending" />;
  }

  if (requireAdmin && userProfile?.role !== 'admin') {
    return <Navigate to="/" />;
  }

  // FIX: Prevent admin from accessing client dashboard
  if (!requireAdmin && userProfile?.role === 'admin') {
    const saved = getSavedPath();
    return <Navigate to={saved?.startsWith('/admin') ? saved : '/admin'} replace />;
  }

  // Restore last path on PWA reload
  if (typeof window !== 'undefined' && window.location.pathname === '/') {
    const saved = getSavedPath();
    if (saved && saved !== '/' && !saved.startsWith('/admin') && !requireAdmin) {
      return <Navigate to={saved} replace />;
    }
  }

  return children;
};

// Main app routes
const getSavedPath = () => {
  const path = localStorage.getItem('lastPath');
  const time = parseInt(localStorage.getItem('lastPathTime') || '0');
  if (path && (Date.now() - time) < 8 * 60 * 60 * 1000) return path;
  return null;
};

const RouteTracker = () => {
  const location = useLocation();
  React.useEffect(() => {
    if (location.pathname !== '/login' && location.pathname !== '/pending') {
      localStorage.setItem('lastPath', location.pathname);
      localStorage.setItem('lastPathTime', Date.now().toString());
    }
  }, [location]);
  return null;
};

const AppRoutes = () => {
  const { currentUser, userProfile, loading } = useAuth();

  // --- CAMBIO AQUÍ: Solo mostrar pantalla de carga si `loading` es true ---
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }
  // --- FIN CAMBIO ---

  const savedPath = getSavedPath();

  return (
    <Routes>
      <Route 
        path="/login" 
        element={
          currentUser ? (
            userProfile?.status === 'pending' ? (
              <Navigate to="/pending" />
            ) : userProfile?.role === 'admin' ? (
              <Navigate to={savedPath?.startsWith('/admin') ? savedPath : '/admin'} />
            ) : (
              <Navigate to={savedPath && !savedPath.startsWith('/admin') ? savedPath : '/'} />
            )
          ) : (
            <Login />
          )
        } 
      />

      <Route 
        path="/pending" 
        element={
          currentUser && userProfile?.status === 'pending' ? (
            <PendingApproval />
          ) : (
            <Navigate to="/login" />
          )
        } 
      />

      <Route
        path="/admin/*"
        element={
          <ProtectedRoute requireAdmin={true}>
            <>
              <RouteTracker />
              <AdminDashboard />
            </>
          </ProtectedRoute>
        }
      />

      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <>
              <RouteTracker />
              <ClientDashboard />
            </>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

export default App;